'use client';

import { cn } from '@workspace/lib';
import { Typography } from '@workspace/ui/common';
import { processHeading } from '@workspace/lib/heading-utils';
import { getBackgroundClass } from '@workspace/lib/helper';
import { CustomPortableText } from '@workspace/ui/PortableText';
import { textSectionVariants } from '@workspace/blocks/TextSection/textSection.variants';
import type { TextSectionProps } from './text-section.types';
import { getColumnClasses, getTextAlignClasses } from './TextSection.lib';

export function TextSection({ block }: TextSectionProps) {
  const { heading, content, textAlign = 'left', backgroundColor, layout = 'single', columns = [] } = block;

  const { text: resolvedHeadingText } = processHeading(heading);

  const textAlignClasses = getTextAlignClasses(textAlign);
  const columnClasses = getColumnClasses(layout);
  const backgroundClass = getBackgroundClass(backgroundColor);
  const { section, columns: columnSpacing, content: contentTypography } = textSectionVariants();

  return (
    <div className={cn('w-full', backgroundClass)}>
      <section className={cn('porta-container', section())}>
        <div className="mx-auto">
          <div>
            {resolvedHeadingText && (
              <Typography
                variant="h4"
                className={cn('text-2xl! !md:text-4xl font-bold mb-6 text-gray-900', textAlignClasses)}
              >
                {resolvedHeadingText}
              </Typography>
            )}

            {layout !== 'single' && Array.isArray(columns) && columns.length > 0 ? (
              <div className={cn(columnClasses, columnSpacing())}>
                {columns.slice(0, 2).map((column) => {
                  const { text: columnHeadingText } = processHeading(column?.heading);
                  return (
                    <div key={column._key} className="space-y-4">
                      {columnHeadingText && (
                        <Typography
                          variant="h4"
                          className={cn(
                            'text-xl font-semibold text-gray-900 pb-2 max-sm:max-w-screen',
                            getTextAlignClasses(column?.textAlign || 'left'),
                          )}
                        >
                          {columnHeadingText}
                        </Typography>
                      )}
                      <div
                        className={cn('prose prose-lg max-w-none', getTextAlignClasses(column?.textAlign || 'left'))}
                      >
                        <CustomPortableText className={contentTypography()} value={column?.content || []} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              content && (
                <div className={cn('prose prose-lg max-w-none', textAlignClasses)}>
                  <CustomPortableText className={contentTypography()} value={content} />
                </div>
              )
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
